import { SegmentedControl } from '@mantine/core';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setGroupDetailTab, setListView } from '@/features/analysisMapSlice';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { GroupAnalysisType } from '@/generated/types/navigator/types.gen';
import { useHasGroupRiskSummary } from '@/hooks/useWildfireMode';

type ToggleValue = 'map' | 'list' | 'summary';

export function MapListToggle() {
  const dispatch = useAppDispatch();
  const intl = useClientTerminology();
  const { isListView, selectedAnalysis, activeGroupName, groupDetailTab } =
    useAppSelector((s) => s.analysisMap);
  const hasGroupRiskSummary = useHasGroupRiskSummary();

  // Show the third "Summary" segment only for the 'all' wildfire mode (which
  // exposes the new group risk summary UI), drilled into a network group.
  // 'legacy' / 'fuels-only' customers use the span list — no summary tab.
  const showSummary =
    activeGroupName != null &&
    selectedAnalysis === GroupAnalysisType.WILDFIRE_PRIORITY &&
    hasGroupRiskSummary;

  const value: ToggleValue = !isListView
    ? 'map'
    : showSummary && groupDetailTab === 'summary'
      ? 'summary'
      : 'list';

  const onChange = (next: string) => {
    if (next === 'map') {
      dispatch(setListView(false));
      return;
    }
    dispatch(setListView(true));
    dispatch(setGroupDetailTab(next === 'summary' ? 'summary' : 'spans'));
  };

  return (
    <SegmentedControl
      data-testid="map-list-toggle"
      size="sm"
      radius="xl"
      vars={() => ({
        root: {
          '--sc-padding': '2px 14px',
        },
      })}
      withItemsBorders={false}
      value={value}
      onChange={onChange}
      data={[
        {
          label: (
            <span data-testid="toggle-map">
              {intl.formatMessage({ id: 'action.map' })}
            </span>
          ),
          value: 'map',
        },
        {
          label: (
            <span data-testid="toggle-list">
              {intl.formatMessage({ id: 'action.list' })}
            </span>
          ),
          value: 'list',
        },
        ...(showSummary
          ? [
              {
                label: (
                  <span data-testid="toggle-summary">
                    {intl.formatMessage({ id: 'action.summary' })}
                  </span>
                ),
                value: 'summary',
              },
            ]
          : []),
      ]}
    />
  );
}
